import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowDownRight, ArrowUpRight, Minus, Waves } from "lucide-react";

export function useLakes() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/lakes").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 900_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

const TONE = { good: "#16a34a", watch: "#f6a21c", bad: "#d90429", none: "#9ca3af" };
const NAVY = "#0a0f1c";
const WATER = "#1d6fd8";
const fmt = (n, d = 2) => (n == null ? "—" : n.toFixed(d));

function Gauge({ delta }) {
  // 0 = full pool at the top; show up to 15 ft below
  const pct = delta == null ? 0 : Math.max(0, Math.min(1, 1 - Math.min(0, delta) / -15));
  return (
    <div className="relative h-[118px] w-[40px] shrink-0 overflow-hidden rounded-[4px] border-[3px]" style={{ borderColor: NAVY, background: "#e9eef5" }} data-testid="lake-gauge">
      <div className="absolute inset-x-0 bottom-0" style={{ height: `${Math.round(pct * 100)}%`, background: `linear-gradient(180deg, #5aa5ff 0%, ${WATER} 100%)` }} />
      <div className="absolute inset-x-0 top-0 h-[3px]" style={{ background: "#d90429" }} />
      <div className="absolute inset-x-0" style={{ top: "33%", borderTop: `2px dashed ${NAVY}55` }} />
      <div className="absolute inset-x-0" style={{ top: "66%", borderTop: `2px dashed ${NAVY}55` }} />
    </div>
  );
}

function Spark({ pts }) {
  if (!pts || pts.length < 2) return null;
  const min = Math.min(...pts), max = Math.max(...pts), span = Math.max(0.2, max - min);
  const d = pts.map((v, i) => `${i ? "L" : "M"}${(i / (pts.length - 1)) * 160} ${36 - ((v - min) / span) * 32 + 2}`).join(" ");
  return <svg viewBox="0 0 160 40" className="h-[34px] w-[140px]"><path d={d} fill="none" stroke={WATER} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

export function LakeCard({ lake }) {
  const d = lake.delta, t = lake.trend7;
  const Trend = t == null ? Minus : t > 0.05 ? ArrowUpRight : t < -0.05 ? ArrowDownRight : Minus;
  const trendColor = t == null ? "#5b6675" : t > 0.05 ? "#16a34a" : t < -0.05 ? "#d90429" : "#5b6675";
  return (
    <div className="flex items-stretch gap-4 rounded-[6px] bg-white px-4 py-3 shadow-[0_12px_30px_rgba(10,15,28,0.12)]" style={{ borderLeft: `10px solid ${TONE[lake.ramp?.tone] || TONE.none}` }} data-testid={`lake-card-${lake.id}`}>
      <Gauge delta={d} />
      <div className="min-w-0 flex-1">
        <div className="font-oswald text-[18px] font-bold uppercase tracking-[0.25em] text-[#5b6675]">{lake.region}</div>
        <div className="truncate font-oswald text-[30px] font-black uppercase leading-none tracking-wide text-[#0a0f1c]" data-testid={`lake-name-${lake.id}`}>{lake.name}</div>
        <div className="mt-1 flex items-end gap-3">
          <div className="font-oswald text-[44px] font-black leading-none text-[#0a0f1c]" data-testid={`lake-level-${lake.id}`}>{fmt(lake.level)}<span className="ml-1 text-[20px] font-bold text-[#5b6675]">ft</span></div>
          <div className="pb-1 font-oswald text-[22px] font-bold uppercase leading-none" style={{ color: d == null ? "#5b6675" : d >= -1 ? "#16a34a" : d >= -5 ? "#f6a21c" : "#d90429" }} data-testid={`lake-delta-${lake.id}`}>
            {d == null ? "—" : d >= 0 ? `${fmt(d)} ft above full pool` : `${fmt(-d)} ft below full pool`}
          </div>
        </div>
        <div className="mt-1 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-oswald text-[20px] font-bold uppercase tracking-wider" style={{ color: trendColor }} data-testid={`lake-trend-${lake.id}`}>
            <Trend className="h-5 w-5" /> {t == null ? "7-day: —" : `${t > 0 ? "+" : ""}${fmt(t)} ft · 7 days`}
          </div>
          <Spark pts={lake.spark} />
        </div>
        <div className="mt-1 inline-flex items-center gap-2 rounded-[3px] px-3 py-1 font-oswald text-[18px] font-bold uppercase tracking-wider text-white" style={{ background: TONE[lake.ramp?.tone] || TONE.none }} data-testid={`lake-ramp-${lake.id}`}>
          <Waves className="h-4 w-4" /> {lake.ramp?.label} · full pool {lake.full_pool}
        </div>
      </div>
    </div>
  );
}

export function LakesSlide({ lakes }) {
  const items = (lakes?.items || []).filter((l) => l.level != null);
  if (!items.length) return null;
  return (
    <div data-testid="lakes-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(135deg, #f4f6fa 0%, #dfe6ef 100%)" }}>
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #000 0 2px, transparent 2px 26px)" }} />
      <div className="relative flex h-full w-full flex-col justify-start px-[48px] pt-[8px]">
        <div className="mb-3 flex items-center justify-between font-oswald text-[19px] font-bold uppercase tracking-[0.2em] text-[#5b6675]">
          <span className="flex items-center gap-2"><Waves className="h-5 w-5" style={{ color: WATER }} /> Where the water stands today · red line = full pool</span>
          <span>USGS · U.S. Army Corps of Engineers · ramp outlook is a guide — check USACE for closures</span>
        </div>
        <div className="grid grid-cols-3 content-start gap-4">
          {items.slice(0, 9).map((l) => <LakeCard key={l.id} lake={l} />)}
        </div>
      </div>
    </div>
  );
}
